import * as React from "react";

type PossibleRef<T> = React.Ref<T> | undefined;

function setRef<T>(ref: PossibleRef<T>, value: T) {
  if (typeof ref === "function") {
    return ref(value);
  } else if (ref !== null && ref !== undefined) {
    (ref as React.MutableRefObject<T>).current = value;
  }
}

// WeakMap cache to return stable composed ref functions across re-renders
const composeRefsCache = new WeakMap<object, WeakMap<object, (node: any) => any>>();
const singleRefCache = new WeakMap<object, (node: any) => any>();

function createComposedRef<T>(refs: (React.RefCallback<T> | React.MutableRefObject<T>)[]) {
  let lastNode: T | null = null;

  return (node: T) => {
    // If the node has not changed and is not null, avoid redundant ref invocations
    // that trigger infinite re-render loops in React 19
    if (node === lastNode && node !== null) return;
    lastNode = node;

    let hasCleanup = false;
    const cleanups = refs.map((ref) => {
      const cleanup = setRef(ref, node);
      if (!hasCleanup && typeof cleanup === "function") {
        hasCleanup = true;
      }
      return cleanup;
    });

    if (hasCleanup) {
      return () => {
        lastNode = null;
        for (let i = 0; i < cleanups.length; i++) {
          const cleanup = cleanups[i];
          if (typeof cleanup === "function") {
            cleanup();
          }
        }
      };
    }
  };
}

export function composeRefs<T>(...refs: PossibleRef<T>[]) {
  const validRefs = refs.filter(Boolean) as (React.RefCallback<T> | React.MutableRefObject<T>)[];

  if (validRefs.length === 0) {
    return undefined as unknown as (node: T) => void;
  }

  if (validRefs.length === 1) {
    const single = validRefs[0];
    if (typeof single === "object" || typeof single === "function") {
      let cached = singleRefCache.get(single as object);
      if (!cached) {
        cached = (node: T) => setRef(single, node);
        singleRefCache.set(single as object, cached);
      }
      return cached;
    }
    return (node: T) => setRef(single, node);
  }

  if (
    validRefs.length === 2 &&
    (typeof validRefs[0] === "object" || typeof validRefs[0] === "function") &&
    (typeof validRefs[1] === "object" || typeof validRefs[1] === "function")
  ) {
    const ref1 = validRefs[0] as object;
    const ref2 = validRefs[1] as object;
    let map2 = composeRefsCache.get(ref1);
    if (!map2) {
      map2 = new WeakMap();
      composeRefsCache.set(ref1, map2);
    }
    let cached = map2.get(ref2);
    if (cached) return cached;

    cached = createComposedRef(validRefs);
    map2.set(ref2, cached);
    return cached;
  }

  return createComposedRef(validRefs);
}

export function useComposedRefs<T>(...refs: PossibleRef<T>[]) {
  // eslint-disable-next-line react-hooks/exhaustive-deps
  return React.useCallback(composeRefs(...refs), refs);
}

export default composeRefs;
